import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Filter, ListChecks, Ban, Check, Loader2 } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useDeviceBrowserMode } from '@/hooks/useDeviceBrowserMode';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const browserModeOptions = [
  {
    id: 'filtered-browser',
    title: 'Filtered Browser',
    description: 'Full browsing with content filtering. Sites are organized by category with blocked and allowed lists. You control which categories are accessible, plus maintain custom whitelist and blacklist.',
    details: [
      'Block or allow entire website categories',
      'Add specific sites to whitelist or blacklist',
      'Content filtering for images and videos',
      'Best for users who need web access with guardrails',
    ],
    icon: Filter,
    gradient: 'from-primary to-accent',
  },
  {
    id: 'whitelisted-browser',
    title: 'Whitelisted Browser',
    description: 'Maximum control over web access. Only websites you specifically approve can be visited. Best for younger users or when you want complete oversight of browsing activity.',
    details: [
      'Only approved sites can be accessed',
      'All other websites are blocked by default',
      'Content filtering still applies to allowed sites',
      'Best for younger users needing strict oversight',
    ],
    icon: ListChecks,
    gradient: 'from-primary to-primary-hover',
  },
  {
    id: 'no-browser',
    title: 'No Browser',
    description: 'Browser access is completely disabled. The user cannot browse any websites. Best when web access is not needed or for very young users.',
    details: [
      'All web browsing is disabled',
      'No websites can be accessed',
      'Simplest and most restrictive option',
      'Best for very young users or when web access is not needed',
    ],
    icon: Ban,
    gradient: 'from-destructive to-destructive/80',
  },
];

const BrowserModeSelect: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  const { canChangeDirectly } = useMentorStatus();
  const { currentMode: currentModeId, setBrowserMode } = useDeviceBrowserMode();
  
  const [selectedOption, setSelectedOption] = useState<typeof browserModeOptions[0] | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const currentOption = browserModeOptions.find((opt) => opt.id === currentModeId) || browserModeOptions[0];

  // Redirect mentees/children back - they can't change modes
  React.useEffect(() => {
    if (!canChangeDirectly) {
      navigate(`/${serial}/settings/browser`, { replace: true });
    }
  }, [canChangeDirectly, navigate, serial]);

  const handleOptionSelect = (option: typeof browserModeOptions[0]) => {
    if (option.id === currentModeId) return;
    setSelectedOption(option);
    setShowConfirmation(true);
  };

  const handleConfirmRequest = async () => {
    if (selectedOption) {
      if (canChangeDirectly) {
        setIsSaving(true);
        try {
          await setBrowserMode(selectedOption.id);
          toast({
            title: "Settings Updated",
            description: `Switched to "${selectedOption.title}".`,
          });
          navigate(`/${serial}/settings/browser`);
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to update setting. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsSaving(false);
        }
      } else {
        toast({
          title: "Request Submitted",
          description: `Your request to switch to "${selectedOption.title}" has been submitted.`,
        });
        navigate(`/${serial}/settings/browser`);
      }
    }
    setShowConfirmation(false);
    setSelectedOption(null);
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings/browser`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Select Browser Mode</h1>
            <p className="text-[13px] text-muted-foreground">Choose how web browsing is managed</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-4">
        <p className="text-[13px] text-muted-foreground px-1">
          Select a browser mode to control how websites are accessed on this device. Each mode offers different levels of control and flexibility.
        </p>

        {browserModeOptions.map((option) => {
          const Icon = option.icon;
          const isCurrentMode = option.id === currentModeId;
          
          return (
            <button
              key={option.id}
              onClick={() => !isCurrentMode && handleOptionSelect(option)}
              disabled={isCurrentMode}
              className={`w-full p-5 rounded-2xl border text-left transition-all ${
                isCurrentMode 
                  ? 'border-primary bg-primary/5 cursor-default' 
                  : 'border-border/50 bg-background hover:bg-muted/30 active:scale-[0.99] shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[16px] text-foreground">{option.title}</p>
                    {isCurrentMode && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                        <Check className="w-3 h-3" />
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-muted-foreground leading-relaxed mb-3">
                    {option.description}
                  </p>
                  <ul className="space-y-1.5">
                    {option.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[12px] text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 mt-1.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mode Change Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly 
                ? `Switch to ${selectedOption?.title}?`
                : 'Request Browser Mode Change'
              }
            </AlertDialogTitle>
            <AlertDialogDescription>
              {canChangeDirectly 
                ? `This will change the browser mode to "${selectedOption?.title}".`
                : <>You are requesting to change from "{currentOption.title}" to "{selectedOption?.title}". This request will be sent for approval.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedOption(null)} disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRequest} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BrowserModeSelect;
